import { state, hasAnimation, frameToSeconds } from '../state.ts';
import { markRangePercent } from '../timeline/marks.ts';
import { isVideoExportSupported } from '../export/exportVideo.ts';

export interface ControlbarHandlers {
  onTogglePlay: () => void;
  onScrub: (frame: number) => void;
  onStep: (delta: number) => void;
  onJumpStart: () => void;
  onJumpEnd: () => void;
  onToggleUnit: () => void;
  onMarkStart: () => void;
  onMarkEnd: () => void;
  onSetStart: (frame: number) => void;
  onSetEnd: (frame: number) => void;
  onExportGlb: () => void;
  onExportMp4: () => void;
  /** null = bake the GLB at the timeline fps. */
  onGlbFps: (fps: number | null) => void;
  /** null = export the MP4 at the timeline fps. */
  onMp4Fps: (fps: number | null) => void;
}

/** Format seconds as either "12.372s" or "0:12.372" timecode. */
function formatTime(seconds: number): string {
  if (state.timeUnit === 'timecode') {
    const m = Math.floor(seconds / 60);
    const s = seconds - m * 60;
    return `${m}:${s.toFixed(3).padStart(6, '0')}`;
  }
  return `${seconds.toFixed(3)}s`;
}

/** Zone 3 controls: play/pause, timeline scrubber, frame inputs, marks, exports. */
export function createControlbar(handlers: ControlbarHandlers) {
  const playPause = byId<HTMLButtonElement>('play-pause');
  const jumpStart = byId<HTMLButtonElement>('jump-start');
  const jumpEnd = byId<HTMLButtonElement>('jump-end');
  const stepBack = byId<HTMLButtonElement>('step-back');
  const stepFwd = byId<HTMLButtonElement>('step-fwd');
  const timeline = byId<HTMLInputElement>('timeline');
  const markRange = byId<HTMLDivElement>('mark-range');
  const frameInput = byId<HTMLInputElement>('frame-input');
  const frameTotal = byId<HTMLSpanElement>('frame-total');
  const timeReadout = byId<HTMLSpanElement>('time-readout');
  const unitToggle = byId<HTMLButtonElement>('unit-toggle');
  const markStart = byId<HTMLButtonElement>('mark-start');
  const startInput = byId<HTMLInputElement>('start-input');
  const markEnd = byId<HTMLButtonElement>('mark-end');
  const endInput = byId<HTMLInputElement>('end-input');
  const clipDuration = byId<HTMLSpanElement>('clip-duration');
  const exportGlb = byId<HTMLButtonElement>('export-glb');
  const exportMp4 = byId<HTMLButtonElement>('export-mp4');
  const glbFps = byId<HTMLSelectElement>('glb-fps');
  const mp4Fps = byId<HTMLSelectElement>('mp4-fps');

  playPause.addEventListener('click', () => handlers.onTogglePlay());
  jumpStart.addEventListener('click', () => handlers.onJumpStart());
  jumpEnd.addEventListener('click', () => handlers.onJumpEnd());
  stepBack.addEventListener('click', () => handlers.onStep(-1));
  stepFwd.addEventListener('click', () => handlers.onStep(1));
  unitToggle.addEventListener('click', () => handlers.onToggleUnit());
  timeline.addEventListener('input', () => handlers.onScrub(Number(timeline.value)));
  frameInput.addEventListener('change', () => handlers.onScrub(Number(frameInput.value)));
  markStart.addEventListener('click', () => handlers.onMarkStart());
  markEnd.addEventListener('click', () => handlers.onMarkEnd());
  startInput.addEventListener('change', () => handlers.onSetStart(Number(startInput.value)));
  endInput.addEventListener('change', () => handlers.onSetEnd(Number(endInput.value)));
  exportGlb.addEventListener('click', () => handlers.onExportGlb());
  exportMp4.addEventListener('click', () => handlers.onExportMp4());
  glbFps.addEventListener('change', () =>
    handlers.onGlbFps(glbFps.value === '' ? null : Number(glbFps.value))
  );
  mp4Fps.addEventListener('change', () =>
    handlers.onMp4Fps(mp4Fps.value === '' ? null : Number(mp4Fps.value))
  );

  if (!isVideoExportSupported()) {
    exportMp4.title = 'Video export needs a Chromium browser with WebCodecs (secure context).';
  }

  function update(): void {
    const ready = hasAnimation();
    const busy = state.exporting;

    const navDisabled = !ready || busy;
    playPause.disabled = navDisabled;
    playPause.textContent = state.playing ? 'Pause' : 'Play';
    jumpStart.disabled = navDisabled;
    jumpEnd.disabled = navDisabled;
    stepBack.disabled = navDisabled;
    stepFwd.disabled = navDisabled;
    unitToggle.disabled = navDisabled;

    timeline.disabled = navDisabled;
    timeline.max = String(state.totalFrames);
    timeline.value = String(state.currentFrame);

    frameInput.disabled = navDisabled;
    frameInput.max = String(state.totalFrames);
    if (document.activeElement !== frameInput) frameInput.value = String(state.currentFrame);
    frameTotal.textContent = `/ ${state.totalFrames}`;

    // Time readout (seconds/ms or timecode) + the unit toggle's label.
    const curT = frameToSeconds(state.currentFrame);
    const totalT = frameToSeconds(state.totalFrames);
    timeReadout.textContent = `${formatTime(curT)} / ${formatTime(totalT)}`;
    unitToggle.textContent = state.timeUnit === 'timecode' ? 'm:s' : 's';

    // Marked-clip length in both frames and time.
    const clipFrames = Math.max(0, state.endFrame - state.startFrame);
    clipDuration.textContent = ready ? `Clip: ${formatTime(frameToSeconds(clipFrames))} · ${clipFrames}f` : '';

    markStart.disabled = !ready || busy;
    markEnd.disabled = !ready || busy;
    startInput.disabled = !ready || busy;
    endInput.disabled = !ready || busy;
    startInput.max = String(state.totalFrames);
    endInput.max = String(state.totalFrames);
    if (document.activeElement !== startInput) startInput.value = String(state.startFrame);
    if (document.activeElement !== endInput) endInput.value = String(state.endFrame);

    exportGlb.disabled = !ready || busy;
    exportMp4.disabled = !ready || busy || !isVideoExportSupported();
    glbFps.disabled = !ready || busy;
    const glbValue = state.glbExportFps === null ? '' : String(state.glbExportFps);
    if (glbFps.value !== glbValue) glbFps.value = glbValue;
    glbFps.querySelectorAll('option').forEach((opt) => {
      // A bake rate above the timeline fps would just duplicate frames.
      opt.disabled = opt.value !== '' && Number(opt.value) > state.fps;
    });
    mp4Fps.disabled = !ready || busy || !isVideoExportSupported();
    const mp4Value = state.videoExportFps === null ? '' : String(state.videoExportFps);
    if (mp4Fps.value !== mp4Value) mp4Fps.value = mp4Value;
    mp4Fps.querySelectorAll('option').forEach((opt) => {
      opt.disabled = opt.value !== '' && Number(opt.value) > state.fps;
    });

    // Position the marked-range highlight over the slider track.
    const { left, width } = markRangePercent();
    if (ready && width > 0) {
      markRange.hidden = false;
      markRange.style.left = `${left}%`;
      markRange.style.width = `${width}%`;
    } else {
      markRange.hidden = true;
    }
  }

  return { update };
}

function byId<T extends HTMLElement>(id: string): T {
  const el = document.getElementById(id);
  if (!el) throw new Error(`Missing element #${id}`);
  return el as T;
}
